import React, { useState, useEffect } from 'react';
import './UserProfile.css';
import { userService, UserProfile as UserProfileType } from '../../services/userService';
import { authService } from '../../services/authService';
import {
  getAddresses,
  getAddressesSync,
  addAddress,
  deactivateAddress,
  setDefaultAddress,
  Address,
  validatePincode,
  getFormattedStateOptions,
  getFormattedCityOptions,
  validateAddressAgainstMasterData
} from '../../utils/addressUtils';

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Helper function to format address display
  const formatAddressForDisplay = (address: Address): string => {
    const parts = [
      address.line1 || address.addressLine1,
      address.line2 || address.addressLine2,
      address.landmark,
      address.cityName || `City ID: ${address.city}`, // Use cityName if available, fallback to ID
      address.stateName || `State ID: ${address.state}`, // Use stateName if available, fallback to ID
      address.postalCode || address.pincode
    ].filter(Boolean);
    
    return parts.join(', ');
  };
  
  // Form states - updated to match API structure
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: ''
  });
  
  const [addressForm, setAddressForm] = useState({
    type: 'home' as Address['type'],
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    isDefault: false,
    // Master data fields
    selectedStateId: 0,
    selectedCityId: 0,
    // Contact information for bookings
    isBookingForOthers: false,    // Checkbox state
    contactName: '',              // Contact person name
    contactPhone: '',             // Contact person phone
  });

  // Master data states
  const [stateOptions, setStateOptions] = useState<Array<{
    value: number;
    label: string;
    code: string;
  }>>([]);
  const [cityOptions, setCityOptions] = useState<Array<{
    value: number;
    label: string;
    stateId: number;
  }>>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setIsLoadingProfile(true);
    try {
      // Check if user is authenticated
      if (!authService.isAuthenticated()) {
        console.log('User not authenticated, redirecting...');
        // Handle unauthenticated state
        setIsLoadingProfile(false);
        return;
      }

      // Fetch user profile from API
      const userProfile = await userService.getUserProfile();
      setProfile(userProfile);
      setProfileForm({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        dob: userProfile.dob || ''
      });
      
      // Load addresses
      const fetchedAddresses = await getAddresses();
      console.log('👤 UserProfile initial load received:', fetchedAddresses);
      setAddresses(fetchedAddresses);
      console.log('👤 UserProfile initial state set with:', fetchedAddresses.length, 'addresses');
    } catch (error) {
      console.error('Error loading user data:', error);
      
      // Try to get cached profile as fallback
      const cachedProfile = userService.getCachedUserProfile();
      if (cachedProfile) {
        setProfile(cachedProfile);
        setProfileForm({
          firstName: cachedProfile.firstName || '',
          lastName: cachedProfile.lastName || '',
          email: cachedProfile.email || '',
          phone: cachedProfile.phone || '',
          dob: cachedProfile.dob || ''
        });
      }
      
      // Load addresses from local storage as fallback
      setAddresses(getAddressesSync());
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Helper function to refresh addresses locally without triggering events
  const refreshAddresses = async () => {
    if (isLoadingAddresses) return; // Prevent multiple simultaneous calls
    
    setIsLoadingAddresses(true);
    try {
      const addresses = await getAddresses();
      console.log('👤 UserProfile refreshAddresses received:', addresses);
      setAddresses(addresses);
      console.log('👤 UserProfile state set with:', addresses.length, 'addresses');
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddresses(getAddressesSync());
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  // Master data loading functions
  const loadStates = async () => {
    setIsLoadingStates(true);
    try {
      const states = await getFormattedStateOptions();
      setStateOptions(states);
      console.log('✅ Loaded states:', states.length);
    } catch (error) {
      console.error('❌ Error loading states:', error);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const loadCities = async (stateId: number) => {
    setIsLoadingCities(true);
    try {
      const cities = await getFormattedCityOptions(stateId);
      setCityOptions(cities);
      console.log(`✅ Loaded cities for state ${stateId}:`, cities.length);
    } catch (error) {
      console.error('❌ Error loading cities:', error);
      setCityOptions([]);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load cities when state changes
  useEffect(() => {
    if (addressForm.selectedStateId > 0) {
      loadCities(addressForm.selectedStateId);
    } else {
      setCityOptions([]);
      setAddressForm(prev => ({ ...prev, selectedCityId: 0, city: '' }));
    }
  }, [addressForm.selectedStateId]);

  // Update state name when state selection changes
  useEffect(() => {
    if (addressForm.selectedStateId > 0) {
      const selectedState = stateOptions.find(state => state.value === addressForm.selectedStateId);
      if (selectedState) {
        setAddressForm(prev => ({ ...prev, state: selectedState.label }));
      }
    }
  }, [addressForm.selectedStateId, stateOptions]);

  // Update city name when city selection changes
  useEffect(() => {
    if (addressForm.selectedCityId > 0) {
      const selectedCity = cityOptions.find(city => city.value === addressForm.selectedCityId);
      if (selectedCity) {
        setAddressForm(prev => ({ ...prev, city: selectedCity.label }));
      }
    }
  }, [addressForm.selectedCityId, cityOptions]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = parseInt(e.target.value);
    setAddressForm(prev => ({
      ...prev,
      selectedStateId: stateId,
      selectedCityId: 0, // Reset city when state changes
      city: ''
    }));
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = parseInt(e.target.value);
    setAddressForm(prev => ({
      ...prev,
      selectedCityId: cityId
    }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profile) {
      try {
        // Update user profile via API - exclude email for security
        const updatedProfile = await userService.updateUserProfile(profile.userUuid, {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phone,
          dob: profileForm.dob || undefined
        });
        
        setProfile(updatedProfile);
        setIsEditingProfile(false);
        
        // Show success message
        console.log('✅ Profile updated successfully');
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
  };

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!validatePincode(addressForm.pincode)) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }

    if (!addressForm.city || !addressForm.state) {
      alert('Please select both state and city');
      return;
    }

    // Optional: Validate address against master data
    try {
      const validation = await validateAddressAgainstMasterData(addressForm.state, addressForm.city);
      if (!validation.isValid) {
        const confirmProceed = window.confirm(
          'The selected state/city combination might not be accurate. Do you want to proceed anyway?'
        );
        if (!confirmProceed) {
          return;
        }
      }
    } catch (error) {
      console.log('Address validation failed, proceeding anyway:', error);
    }
    
    try {
      const addressData = {
        label: addressForm.name,
        addressType: addressForm.type,
        line1: addressForm.addressLine1,
        line2: addressForm.addressLine2,
        // Use master data IDs in state/city fields (not text)
        state: addressForm.selectedStateId || 0,   // Master data state ID  
        city: addressForm.selectedCityId || 0,     // Master data city ID
        postalCode: addressForm.pincode,
        landmark: addressForm.landmark,
        isDefault: addressForm.isDefault,
        default: addressForm.isDefault,
        isActive: true, // New addresses are active by default
        // Include contact information if provided
        contactName: addressForm.isBookingForOthers ? addressForm.contactName : undefined,
        contactPhone: addressForm.isBookingForOthers ? addressForm.contactPhone : undefined
      };

      await addAddress(addressData);
      setIsAddingAddress(false);
      console.log('✅ Address added successfully');
      
      // Refresh addresses list
      await refreshAddresses();
      
      resetAddressForm();
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const resetAddressForm = () => {
    setAddressForm({
      type: 'home',
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      isDefault: false,
      selectedStateId: 0,
      selectedCityId: 0,
      isBookingForOthers: false,
      contactName: '',
      contactPhone: ''
    });
  };



  const handleDeleteAddress = async (addressId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this address? It will be removed from your list but preserved in our records.'
    );
    
    if (confirmed) {
      try {
        setIsLoadingAddresses(true);
        
        // Find the address to get the addressUuid
        const addressToDelete = addresses.find(addr => addr.id === addressId);
        const addressIdentifier = addressToDelete?.addressUuid || addressId;
        console.log('🔍 Using address identifier for delete:', addressIdentifier);
        
        await deactivateAddress(addressIdentifier);
        console.log('✅ Address deactivated successfully');
        // Refresh addresses list
        await refreshAddresses();
      } catch (error) {
        console.error('Error deactivating address:', error);
        alert('Failed to delete address. Please try again.');
      } finally {
        setIsLoadingAddresses(false);
      }
    }
  };

  const handleSetDefault = async (addressId: string) => {
    try {
      setIsLoadingAddresses(true);
      
      // Find the address to get the addressUuid
      const addressToSetDefault = addresses.find(addr => addr.id === addressId);
      const addressIdentifier = addressToSetDefault?.addressUuid || addressId;
      console.log('🔍 Using address identifier for set default:', addressIdentifier);
      console.log('⭐ Setting address as default:', addressIdentifier);
      
      await setDefaultAddress(addressIdentifier);
      console.log('✅ Default address updated successfully');
      // Refresh addresses list
      await refreshAddresses();
    } catch (error) {
      console.error('Error setting default address:', error);
      alert('Failed to set default address. Please try again.');
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const cancelEdit = () => {
    setIsAddingAddress(false);
    resetAddressForm();
  };

  if (isLoadingProfile) {
    return (
      <div className="user-profile-page">
        <div className="profile-container">
          <div className="loading-state">
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile-page">
        <div className="profile-container">
          <div className="error-state">
            <p>Unable to load profile. Please try refreshing the page.</p>
            <button onClick={loadUserData} className="retry-btn">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your personal information and addresses</p>
        </div>

        {/* User Information Section */}
        <div className="profile-section">
          <div className="section-header">
            <h2>Personal Information</h2>
            <button 
              onClick={() => setIsEditingProfile(!isEditingProfile)}
              className="edit-btn"
            >
              {isEditingProfile ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {isEditingProfile ? (
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={profileForm.firstName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s.''-]/g, '').slice(0, 50);
                      setProfileForm(prev => ({ ...prev, firstName: value }));
                    }}
                    placeholder="Enter first name"
                    maxLength={50}
                    required
                  />
                  <small style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6c757d', 
                    fontStyle: 'italic' 
                  }}>
                    Letters only, maximum 50 characters
                  </small>
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={profileForm.lastName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s.''-]/g, '').slice(0, 50);
                      setProfileForm(prev => ({ ...prev, lastName: value }));
                    }}
                    placeholder="Enter last name"
                    maxLength={50}
                    required
                  />
                  <small style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6c757d', 
                    fontStyle: 'italic' 
                  }}>
                    Letters only, maximum 50 characters
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    disabled
                    title="Email cannot be changed as it's used for login"
                  />
                  <small className="field-note">Email cannot be changed as it's used for login</small>
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9+\-() ]/g, '').slice(0, 15);
                      setProfileForm(prev => ({ ...prev, phone: value }));
                    }}
                    placeholder="Enter phone number"
                    maxLength={15}
                    inputMode="tel"
                    required
                  />
                  <small style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6c757d', 
                    fontStyle: 'italic' 
                  }}>
                    Numbers and phone symbols only, maximum 15 characters
                  </small>
                </div>
              </div>
              <div className="form-group">
                <label>Date of Birth (Optional)</label>
                <input
                  type="date"
                  value={profileForm.dob}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, dob: e.target.value }))}
                />
              </div>
              <button type="submit" className="save-btn">Save Changes</button>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-item">
                <span className="label">Name:</span>
                <span className="value">{profile.fullName}</span>
              </div>
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{profile.email || 'Not provided'}</span>
              </div>
              <div className="info-item">
                <span className="label">Phone:</span>
                <span className="value">{profile.phone || 'Not provided'}</span>
              </div>
              <div className="info-item">
                <span className="label">Date of Birth:</span>
                <span className="value">{profile.dob ? new Date(profile.dob).toLocaleDateString() : 'Not provided'}</span>
              </div>
              <div className="info-item">
                <span className="label">Member Since:</span>
                <span className="value">{new Date(profile.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Addresses Section */}
        <div className="profile-section">
          <div className="section-header">
            <h2>Saved Addresses</h2>
            <button 
              onClick={() => setIsAddingAddress(true)}
              className="add-btn"
            >
              Add New Address
            </button>
          </div>

          {/* Add/Edit Address Form */}
          {isAddingAddress && (
            <div className="address-form-container">
              <h3>Add New Address</h3>
              <form onSubmit={handleAddressSubmit} className="address-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Address Type</label>
                    <select
                      value={addressForm.type}
                      onChange={(e) => setAddressForm(prev => ({ ...prev, type: e.target.value as Address['type'] }))}
                    >
                      <option value="home">Home</option>
                      <option value="work">Work</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Address Label</label>
                    <input
                      type="text"
                      value={addressForm.name}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, 50);
                        setAddressForm(prev => ({ ...prev, name: value }));
                      }}
                      placeholder="e.g., Home, Office, Mom's House"
                      maxLength={50}
                      required
                    />
                    <small style={{ 
                      display: 'block', 
                      marginTop: '4px', 
                      fontSize: '12px', 
                      color: '#6c757d', 
                      fontStyle: 'italic' 
                    }}>
                      Maximum 50 characters
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Address Line 1</label>
                  <input
                    type="text"
                    value={addressForm.addressLine1}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      setAddressForm(prev => ({ ...prev, addressLine1: value }));
                    }}
                    placeholder="House/Flat number, Building name"
                    maxLength={100}
                    required
                  />
                  <small style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6c757d', 
                    fontStyle: 'italic' 
                  }}>
                    Maximum 100 characters
                  </small>
                </div>

                <div className="form-group">
                  <label>Address Line 2 (Optional)</label>
                  <input
                    type="text"
                    value={addressForm.addressLine2}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      setAddressForm(prev => ({ ...prev, addressLine2: value }));
                    }}
                    placeholder="Street, Area, Locality"
                    maxLength={100}
                  />
                  <small style={{ 
                    display: 'block', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6c757d', 
                    fontStyle: 'italic' 
                  }}>
                    Maximum 100 characters
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>State</label>
                    <select
                      value={addressForm.selectedStateId}
                      onChange={handleStateChange}
                      required
                      disabled={isLoadingStates}
                    >
                      <option value={0}>
                        {isLoadingStates ? 'Loading states...' : 'Select State'}
                      </option>
                      {stateOptions.map(state => (
                        <option key={state.value} value={state.value}>
                          {state.label} ({state.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <select
                      value={addressForm.selectedCityId}
                      onChange={handleCityChange}
                      required
                      disabled={isLoadingCities || addressForm.selectedStateId === 0}
                    >
                      <option value={0}>
                        {isLoadingCities 
                          ? 'Loading cities...' 
                          : addressForm.selectedStateId === 0 
                            ? 'Select State First' 
                            : 'Select City'
                        }
                      </option>
                      {cityOptions.map(city => (
                        <option key={city.value} value={city.value}>
                          {city.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>PIN Code</label>
                    <input
                      type="text"
                      value={addressForm.pincode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                        setAddressForm(prev => ({ ...prev, pincode: value }));
                      }}
                      placeholder="6-digit PIN code"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      inputMode="numeric"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Landmark (Optional)</label>
                  <input
                    type="text"
                    value={addressForm.landmark}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, landmark: e.target.value }))}
                    placeholder="e.g., Near Metro Station, Opposite Park"
                  />
                </div>

                {/* Contact Information Section */}
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addressForm.isBookingForOthers}
                      onChange={(e) => setAddressForm(prev => ({ 
                        ...prev, 
                        isBookingForOthers: e.target.checked,
                        // Clear contact fields if unchecked
                        contactName: e.target.checked ? prev.contactName : '',
                        contactPhone: e.target.checked ? prev.contactPhone : ''
                      }))}
                    />
                    This address is for booking services for someone else
                  </label>
                </div>

                {/* Contact Fields - Only shown when checkbox is checked */}
                {addressForm.isBookingForOthers && (
                  <div className="contact-fields">
                    <div className="contact-info-header">
                      <h4>Contact Information</h4>
                      <p className="contact-info-description">
                        Provide contact details for the person at this address
                      </p>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Contact Name</label>
                        <input
                          type="text"
                          value={addressForm.contactName}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z\s.''-]/g, '').slice(0, 50);
                            setAddressForm(prev => ({ ...prev, contactName: value }));
                          }}
                          placeholder="Name of person at this address"
                          maxLength={50}
                        />
                        <small style={{ 
                          display: 'block', 
                          marginTop: '4px', 
                          fontSize: '12px', 
                          color: '#6c757d', 
                          fontStyle: 'italic' 
                        }}>
                          Letters only, maximum 50 characters
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Contact Phone</label>
                        <input
                          type="tel"
                          value={addressForm.contactPhone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9+\-() ]/g, '').slice(0, 15);
                            setAddressForm(prev => ({ ...prev, contactPhone: value }));
                          }}
                          placeholder="Phone number for coordination"
                          maxLength={15}
                          inputMode="tel"
                        />
                        <small style={{ 
                          display: 'block', 
                          marginTop: '4px', 
                          fontSize: '12px', 
                          color: '#6c757d', 
                          fontStyle: 'italic' 
                        }}>
                          Numbers and phone symbols only, maximum 15 characters
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addressForm.isDefault}
                      onChange={(e) => setAddressForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                    />
                    Make this my default address
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-btn">
                    Save Address
                  </button>
                  <button type="button" onClick={cancelEdit} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Address List */}
          <div className="addresses-grid">
            {addresses.length === 0 ? (
              <div className="no-addresses">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L3 9V20C3 20.55 3.45 21 4 21H9V12H15V21H20C20.55 21 21 20.55 21 20V9L12 2Z" fill="currentColor" opacity="0.3"/>
                  <path d="M12 2L3 9V20C3 20.55 3.45 21 4 21H9V12H15V21H20C20.55 21 21 20.55 21 20V9L12 2Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                <h3>No addresses saved</h3>
                <p>Add your first address to get started with bookings</p>
              </div>
            ) : (
              addresses.map((address) => (
                <div key={address.id} className={`address-card ${address.isDefault || address.default ? 'default' : ''}`}>
                  <div className="address-header">
                    <div className="address-type">
                      <span className={`type-badge ${address.addressType || address.type || 'other'}`}>
                        {address.addressType ? address.addressType.charAt(0).toUpperCase() + address.addressType.slice(1) : 'Address'}
                      </span>
                      <h3>{address.label || address.name || 'Address'}</h3>
                    </div>
                    {(address.isDefault || address.default) && (
                      <span className="default-badge">Default</span>
                    )}
                  </div>
                  
                  <div className="address-content">
                    <p>{formatAddressForDisplay(address)}</p>
                    {(address.contactName || address.contactPhone) && (
                      <div className="contact-info">
                        <div className="contact-info-header">
                          <span className="contact-icon">👤</span>
                          <span className="contact-label">Contact Person:</span>
                        </div>
                        <div className="contact-details">
                          {address.contactName && (
                            <span className="contact-name">{address.contactName}</span>
                          )}
                          {address.contactPhone && (
                            <span className="contact-phone">📞 {address.contactPhone}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="address-actions">
                    {!(address.isDefault || address.default) && (
                      <button 
                        onClick={() => handleSetDefault(address.id!)}
                        className="default-btn"
                        disabled={isLoadingAddresses}
                      >
                        {isLoadingAddresses ? 'Setting...' : 'Set as Default'}
                      </button>
                    )}
                    <button 
                      onClick={() => handleDeleteAddress(address.id!)}
                      className="delete-btn"
                      disabled={isLoadingAddresses}
                    >
                      {isLoadingAddresses ? 'Removing...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;